import { CalendarState } from 'react-stately/useCalendarState';
import { DateDuration } from '@internationalized/date';
import React from 'react';
import { DOMAttributes } from '@react-types/shared';
import { useLocale } from 'react-aria/I18nProvider';

/**
 * A little generalized but also simplified version of useCalendarGrid, to be used for months and
 * years view. The keyboard navigation in useCalendarGrid is around the assumption of grid cells
 * representing days, but not other durations like month or year.
 *
 * NOTE: only simple arrow key navigation is supported in this hook, and Home/End PageUp/PageDown
 * are left out to keep it simple.
 */
export function useCustomDurationCalendarGrid(
  {
    cellsDuration,
    perRow = 3,
  }: {
    cellsDuration: keyof DateDuration;
    perRow?: number;
  },
  state: CalendarState
): { gridProps: DOMAttributes } {
  const { direction } = useLocale();

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        state.selectFocusedDate();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        e.stopPropagation();
        if (direction === 'rtl') {
          state.setFocusedDate(state.focusedDate.add({ [cellsDuration]: 1 }));
        } else {
          state.setFocusedDate(state.focusedDate.add({ [cellsDuration]: -1 }));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        state.setFocusedDate(
          state.focusedDate.add({ [cellsDuration]: -perRow })
        );
        break;
      case 'ArrowRight':
        e.preventDefault();
        e.stopPropagation();
        if (direction === 'rtl') {
          state.setFocusedDate(state.focusedDate.add({ [cellsDuration]: -1 }));
        } else {
          state.setFocusedDate(state.focusedDate.add({ [cellsDuration]: 1 }));
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        state.setFocusedDate(
          state.focusedDate.add({ [cellsDuration]: perRow })
        );
        break;
    }
  };
  const gridProps = {
    role: 'grid',
    'aria-readonly': state.isReadOnly || undefined,
    'aria-disabled': state.isDisabled || undefined,
    onKeyDown,
    onFocus: () => state.setFocused(true),
    onBlur: () => state.setFocused(false),
  };
  return { gridProps };
}
